/**
 * Help & Support widget — opens from profile menu; product-aware copy and categories.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  X,
  Shield,
  Paperclip,
  Send,
  FileText,
  CreditCard,
  Upload,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useSupportWidget } from '../../context/SupportWidgetContext';
import {
  WORKSPACE_PRODUCTS,
  detectWorkspaceProduct,
  getFundGeniusManagePlanPath,
  getProductSupportConfig,
  resolveFundGeniusBasePath,
} from '../../config/productWorkspaceConfig';
import SupportTypingIndicator from './SupportTypingIndicator';
import './SupportWidget.css';

const SW = 'supportWidget';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const ACCEPTED_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

const hasPremiumSupportEntitlement = (user) => {
  const tier = String(user?.subscription_tier || user?.plan || user?.tier || '').toLowerCase();
  return ['premium', 'ai_assisted', 'specialist_pro', 'agency', 'professional', 'pro'].some(
    (k) => tier.includes(k)
  );
};

const SupportWidget = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const { isOpen, product: contextProduct, closeSupportWidget, composerRef } = useSupportWidget();

  const product = contextProduct || detectWorkspaceProduct({ pathname: location.pathname });
  const supportConfig = getProductSupportConfig(product);
  const isFundGenius = product === WORKSPACE_PRODUCTS.FUNDGENIUS_HQ;
  const isAgencyProduct = product === WORKSPACE_PRODUCTS.AGENCY;
  const usesProductStrings = isFundGenius || isAgencyProduct;
  const strings = supportConfig.strings || {};
  const categoryMap = useMemo(
    () => Object.fromEntries(supportConfig.categories.map((c) => [c.key, c])),
    [supportConfig.categories]
  );

  const [categoryKey, setCategoryKey] = useState(supportConfig.defaultCategoryKey);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [typingMode, setTypingMode] = useState(null);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploadRouteSelected, setUploadRouteSelected] = useState(null);
  const [relatedTicketId, setRelatedTicketId] = useState('');
  const [openTickets] = useState([]);

  const supportFileRef = useRef(null);
  const genericFileRef = useRef(null);
  const messagesEndRef = useRef(null);

  const isPremium = hasPremiumSupportEntitlement(user);

  useEffect(() => {
    if (!isOpen) return;
    setCategoryKey(supportConfig.defaultCategoryKey);
    setMessages([]);
    setDraft('');
    setPendingAttachment(null);
    setUploadRouteSelected(null);
    setRelatedTicketId('');
  }, [isOpen, product, supportConfig.defaultCategoryKey]);

  const label = useCallback(
    (selfRepairKey, fundGeniusStringKey) => {
      if (usesProductStrings && strings[fundGeniusStringKey]) {
        return strings[fundGeniusStringKey];
      }
      return t(`${SW}.${selfRepairKey}`);
    },
    [usesProductStrings, strings, t]
  );

  const categoryLabel = useCallback(
    (category) => {
      if (category.label) return category.label;
      return t(`${SW}.category.${category.labelKey}`);
    },
    [t]
  );

  const fundGeniusPathFor = useCallback(
    (segment) => resolveFundGeniusBasePath(location.pathname)(segment),
    [location.pathname]
  );

  const documentsPath = isFundGenius
    ? fundGeniusPathFor('documents')
    : supportConfig.documentsPath;
  const billingPath = isFundGenius
    ? getFundGeniusManagePlanPath(location.pathname)
    : supportConfig.billingPath;
  const creditReportPath = supportConfig.creditReportPath;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [isOpen, messages, typingMode, scrollToBottom]);

  const insertUploadOptions = useCallback(() => {
    setMessages((prev) => {
      if (prev.some((m) => m.type === 'upload_options')) return prev;
      return [
        ...prev,
        {
          id: `upload-${Date.now()}`,
          role: 'system',
          type: 'upload_options',
        },
      ];
    });
    scrollToBottom();
  }, [scrollToBottom]);

  const clearUploadOptions = useCallback(() => {
    setMessages((prev) => prev.filter((m) => m.type !== 'upload_options'));
  }, []);

  const buildSupportPayload = useCallback(
    (messageText, extra = {}) => ({
      product: supportConfig.productId,
      module: categoryMap[categoryKey]?.value || 'general_question',
      current_route: location.pathname,
      user_id: user?.user_id || user?.id,
      agency_id: user?.agency_id || null,
      support_ticket_id: relatedTicketId || null,
      category: categoryMap[categoryKey]?.value || 'general_question',
      message: messageText,
      attachment_ids: extra.attachment_ids || [],
      related_ticket_id: categoryKey === 'existingTicket' ? relatedTicketId || null : null,
      upload_route_selected: uploadRouteSelected,
      source: 'support_widget',
      created_at: new Date().toISOString(),
      ...extra,
    }),
    [supportConfig.productId, categoryMap, categoryKey, location.pathname, user, relatedTicketId, uploadRouteSelected]
  );

  const sendToAssistant = useCallback(
    async (text, attachmentMeta = null) => {
      const userMsg = {
        id: `u-${Date.now()}`,
        role: 'user',
        content: text,
        attachment: attachmentMeta,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev.filter((m) => m.type !== 'upload_options'), userMsg]);
      setDraft('');
      setPendingAttachment(null);
      setTypingMode('ai');

      const payload = buildSupportPayload(text, {
        attachment_preview: attachmentMeta?.name || null,
      });

      try {
        const headers = getAuthHeaders();
        const response = await axios.post(
          `${API}/ai/chat`,
          {
            message: `[Support Widget | ${payload.category}]\n${text}`,
            mode: supportConfig.chatMode,
            support_context: payload,
          },
          { headers }
        );
        const reply = response.data?.response || label('typing.ai', 'typingAi');
        setMessages((prev) => [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: reply },
        ]);
      } catch (err) {
        console.error('Support message failed:', err);
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: 'assistant',
            content: label('typing.ai', 'typingAi'),
          },
        ]);
      } finally {
        setTypingMode(null);
        scrollToBottom();
      }
    },
    [buildSupportPayload, getAuthHeaders, label, scrollToBottom, supportConfig.chatMode]
  );

  const handleSend = () => {
    const text = draft.trim();
    if (!text && !pendingAttachment) return;
    const body = text || label('attachment.preview', 'attachmentPreview');
    sendToAssistant(
      body,
      pendingAttachment
        ? { name: pendingAttachment.name, type: pendingAttachment.type, size: pendingAttachment.size }
        : null
    );
  };

  const handleFileChosen = (file, route) => {
    if (!file) return;
    if (!ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingAttachment({
        name: file.name,
        type: file.type,
        size: file.size,
        previewUrl: file.type.startsWith('image/') ? reader.result : null,
      });
      setUploadRouteSelected(route);
      clearUploadOptions();
    };
    reader.readAsDataURL(file);
  };

  const uploadOptions = useMemo(() => {
    const options = [
      {
        key: isFundGenius ? 'clientDocument' : 'personalDocument',
        icon: FileText,
        route: isFundGenius ? 'client_document' : 'personal_document',
        titleKey: isFundGenius ? 'uploadClientDocumentTitle' : 'upload.personalDocument.title',
        descriptionKey: isFundGenius ? 'uploadClientDocumentDescription' : 'upload.personalDocument.description',
        ctaKey: isFundGenius ? 'uploadClientDocumentCta' : 'upload.personalDocument.cta',
        onClick: () => {
          clearUploadOptions();
          closeSupportWidget();
          navigate(documentsPath);
        },
      },
      {
        key: 'supportAttachment',
        icon: Paperclip,
        route: 'support_attachment',
        titleKey: 'upload.supportAttachment.title',
        descriptionKey: 'upload.supportAttachment.description',
        ctaKey: 'upload.supportAttachment.cta',
        onClick: () => {
          setUploadRouteSelected('support_attachment');
          supportFileRef.current?.click();
        },
      },
    ];

    if (supportConfig.includeCreditReportUpload && creditReportPath) {
      options.push({
        key: 'creditReport',
        icon: CreditCard,
        route: 'credit_report',
        titleKey: 'upload.creditReport.title',
        descriptionKey: 'upload.creditReport.description',
        ctaKey: 'upload.creditReport.cta',
        onClick: () => {
          clearUploadOptions();
          closeSupportWidget();
          navigate(creditReportPath);
        },
      });
    }

    options.push({
      key: 'none',
      icon: Upload,
      route: 'none_of_the_above',
      titleKey: isFundGenius ? 'uploadNoneTitle' : 'upload.none.title',
      descriptionKey: isFundGenius ? 'uploadNoneDescription' : 'upload.none.description',
      ctaKey: isFundGenius ? 'uploadNoneCta' : 'upload.none.cta',
      onClick: () => {
        setUploadRouteSelected('none_of_the_above');
        genericFileRef.current?.click();
      },
    });

    return options;
  }, [
    clearUploadOptions,
    closeSupportWidget,
    creditReportPath,
    documentsPath,
    isFundGenius,
    navigate,
    supportConfig.includeCreditReportUpload,
  ]);

  const uploadOptionLabel = useCallback(
    (option, field) => {
      if (isFundGenius && strings[option[`${field}Key`]]) {
        return strings[option[`${field}Key`]];
      }
      return t(`${SW}.${option[`${field}Key`]}`);
    },
    [isFundGenius, strings, t]
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="support-widget-backdrop"
        role="presentation"
        onClick={closeSupportWidget}
        data-testid="support-widget-backdrop"
      />
      <div
        className="support-widget-modal support-widget"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-widget-title"
        data-testid="support-widget"
        data-product={product}
      >
        <header className="support-widget-header">
          <div className="support-widget-header-top">
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div className="support-widget-avatars" aria-hidden="true">
                {['JM', 'KS', 'AR'].map((initials) => (
                  <span key={initials} className="support-widget-avatar">
                    {initials}
                  </span>
                ))}
              </div>
              <div>
                <h2 id="support-widget-title" className="support-widget-title">
                  {label('header.title', 'headerTitle')}
                </h2>
                <p className="support-widget-subtitle">{label('header.subtitle', 'headerSubtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              className="support-widget-close"
              onClick={closeSupportWidget}
              aria-label={label('actions.close', 'close')}
              data-testid="support-widget-close"
            >
              <X size={18} />
            </button>
          </div>
          <p className="support-widget-desc">{label('header.description', 'headerDescription')}</p>
        </header>

        <div className="support-widget-body">
          <div className="support-premium-card">
            <Shield size={22} style={{ color: 'var(--sw-emerald-2)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <strong>{label('premium.title', 'premiumTitle')}</strong>
              <p>{label('premium.description', 'premiumDescription')}</p>
            </div>
            <button
              type="button"
              className="support-premium-btn"
              onClick={() => {
                if (isPremium) {
                  setCategoryKey('technical');
                  composerRef.current?.focus?.();
                } else {
                  closeSupportWidget();
                  navigate(billingPath);
                }
              }}
            >
              {isPremium
                ? label('premium.contactPriority', 'premiumContactPriority')
                : label('premium.upgrade', 'premiumUpgrade')}
            </button>
          </div>

          <div className="support-widget-category">
            <label htmlFor="support-category">{label('category.label', 'categoryLabel')}</label>
            <select
              id="support-category"
              className="support-widget-select"
              value={categoryKey}
              onChange={(e) => setCategoryKey(e.target.value)}
              data-testid="support-category-select"
            >
              {supportConfig.categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {categoryLabel(category)}
                </option>
              ))}
            </select>
          </div>

          {categoryKey === 'existingTicket' && (
            <div className="support-ticket-picker">
              <label htmlFor="support-ticket-id">{label('tickets.selectLabel', 'ticketsSelectLabel')}</label>
              <select
                id="support-ticket-id"
                className="support-widget-select"
                value={relatedTicketId}
                onChange={(e) => setRelatedTicketId(e.target.value)}
              >
                <option value="">{label('tickets.empty', 'ticketsEmpty')}</option>
                {openTickets.map((ticket) => (
                  <option key={ticket.id} value={ticket.id}>
                    {ticket.subject || ticket.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="support-chat">
            {messages.map((msg) => {
              if (msg.type === 'upload_options') {
                return (
                  <div key={msg.id} className="support-chat-bubble system">
                    <strong style={{ color: 'var(--sw-heading)' }}>
                      {label('upload.title', 'uploadTitle')}
                    </strong>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--sw-muted)' }}>
                      {label('upload.subtitle', 'uploadSubtitle')}
                    </p>
                    <div className="support-upload-options">
                      {uploadOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                          <div key={option.key} className="support-upload-option">
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                              <Icon size={18} style={{ color: 'var(--sw-emerald-2)', marginTop: 2 }} />
                              <div style={{ flex: 1 }}>
                                <h4>{uploadOptionLabel(option, 'title')}</h4>
                                <p>{uploadOptionLabel(option, 'description')}</p>
                                <button type="button" className="support-upload-cta" onClick={option.onClick}>
                                  {uploadOptionLabel(option, 'cta')} &rsaquo;
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="support-upload-cancel" onClick={clearUploadOptions}>
                      {label('actions.cancel', 'cancel')}
                    </button>
                  </div>
                );
              }

              return (
                <div
                  key={msg.id}
                  className={`support-chat-bubble ${msg.role === 'user' ? 'user' : 'assistant'}`}
                >
                  {msg.content}
                  {msg.attachment?.name && (
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
                      {msg.attachment.name}
                    </div>
                  )}
                </div>
              );
            })}

            {typingMode === 'ai' && (
              <SupportTypingIndicator label={label('typing.ai', 'typingAi')} />
            )}
            {typingMode === 'support' && (
              <SupportTypingIndicator label={label('typing.support', 'typingSupport')} />
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="support-chat-composer">
          {pendingAttachment && (
            <div className="support-attachment-preview">
              {pendingAttachment.previewUrl ? (
                <img
                  src={pendingAttachment.previewUrl}
                  alt=""
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <FileText size={20} />
              )}
              <span>{pendingAttachment.name}</span>
              <button type="button" onClick={() => setPendingAttachment(null)}>
                {label('attachment.remove', 'attachmentRemove')}
              </button>
            </div>
          )}
          <div className="support-composer-tools">
            <button
              type="button"
              className="support-icon-btn"
              onClick={insertUploadOptions}
              aria-label={label('composer.attach', 'composerAttach')}
              data-testid="support-attach-btn"
            >
              <Paperclip size={18} />
            </button>
          </div>
          <div className="support-chat-composer-row">
            <textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={label('composer.placeholder', 'composerPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              data-testid="support-composer-input"
            />
            <button
              type="button"
              className="support-send-btn"
              onClick={handleSend}
              disabled={!draft.trim() && !pendingAttachment}
              aria-label={label('composer.send', 'composerSend')}
              data-testid="support-send-btn"
            >
              <Send size={18} />
            </button>
          </div>
        </div>

        <input
          ref={supportFileRef}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES.join(',')}
          className="hidden"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFileChosen(e.target.files?.[0], 'support_attachment');
            e.target.value = '';
          }}
        />
        <input
          ref={genericFileRef}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES.join(',')}
          className="hidden"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFileChosen(e.target.files?.[0], 'none_of_the_above');
            e.target.value = '';
          }}
        />
      </div>
    </>
  );
};

export default SupportWidget;
